import React, { useRef, useState, useEffect } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Button,
   TextField,
   Box,
   Typography,
   Grid,
   Card,
   CardMedia,
   Checkbox,
   CircularProgress,
   useMediaQuery,
   useTheme,
   IconButton,
   Alert,
   Autocomplete,
   Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { useInfiniteScroll } from '../../hooks/useInfiniteScrollPhotos';

interface Photo {
   id: string;
   url: string;
   title?: string;
}

interface Participant {
   id: string;
   email: string;
   displayName?: string;
   name?: string;
   profileData?: {
      displayName?: string;
      fullName?: string;
      name?: string;
   };
}

interface ParticipantReminderModalProps {
   open: boolean;
   onClose: () => void;
   photos: Photo[];
   selectedPhotos: Set<string>;
   onTogglePhoto: (photoId: string) => void;
   message: string;
   onMessageChange: (message: string) => void;
   onSend: (selectedParticipantIds: string[]) => void;
   sending: boolean;
   members: Participant[];
   loadingPhotos?: boolean;
   hasMorePhotos?: boolean;
   onLoadMorePhotos?: () => void;
}

const MAX_PHOTO_SELECTION = 5;

const ParticipantReminderModal: React.FC<ParticipantReminderModalProps> = ({
   open,
   onClose,
   photos,
   selectedPhotos,
   onTogglePhoto,
   message,
   onMessageChange,
   onSend,
   sending,
   members,
   loadingPhotos = false,
   hasMorePhotos = false,
   onLoadMorePhotos,
}) => {
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
   const photoGridRef = useRef<HTMLDivElement>(null);
   const [selectedParticipants, setSelectedParticipants] = useState<Participant[]>([]);

   // Reset selected participants when modal closes
   useEffect(() => {
      if (!open) {
         setSelectedParticipants([]);
      }
   }, [open]);

   // Debug: Log state when modal opens
   console.log('📸 ParticipantReminderModal state:', {
      open,
      photosCount: photos.length,
      loadingPhotos,
      hasMorePhotos,
      selectedCount: selectedPhotos.size,
      participantsCount: members.length,
   });

   // Use the built-in infinite scroll hook
   useInfiniteScroll(
      () => {
         console.log('🔄 Infinite scroll triggered!', {
            hasMorePhotos,
            loadingPhotos,
            hasCallback: !!onLoadMorePhotos,
         });
         if (hasMorePhotos && !loadingPhotos && onLoadMorePhotos) {
            console.log('✅ Calling onLoadMorePhotos...');
            onLoadMorePhotos();
         } else {
            console.log('❌ Not calling onLoadMorePhotos:', {
               hasMorePhotos,
               loadingPhotos,
               hasCallback: !!onLoadMorePhotos,
            });
         }
      },
      {
         threshold: 200,
         enabled: open && hasMorePhotos && !loadingPhotos,
         containerRef: photoGridRef,
      }
   );

   const handlePhotoToggle = (photoId: string) => {
      if (!selectedPhotos.has(photoId) && selectedPhotos.size >= MAX_PHOTO_SELECTION) {
         // Don't allow selecting more than 5 photos
         return;
      }
      onTogglePhoto(photoId);
   };

   return (
      <Dialog
         open={open}
         onClose={onClose}
         maxWidth='md'
         fullWidth
         fullScreen={isMobile}
         PaperProps={{
            sx: {
               borderRadius: isMobile ? 0 : 2,
               maxHeight: isMobile ? '100vh' : '90vh',
            },
         }}
      >
         <DialogTitle
            sx={{
               display: 'flex',
               justifyContent: 'space-between',
               alignItems: 'center',
               pb: 2,
            }}
         >
            <Typography variant='h6' component='div'>
               Send Reminder to Participants
            </Typography>
            <IconButton edge='end' onClick={onClose} disabled={sending}>
               <CloseIcon />
            </IconButton>
         </DialogTitle>

         <DialogContent dividers>
            {/* Participant Selection Dropdown */}
            <Box sx={{ mb: 3 }}>
               {/* Select All Checkbox */}
               <Box
                  sx={{
                     display: 'flex',
                     alignItems: 'center',
                     mb: 1,
                     p: 1,
                     bgcolor: 'rgba(0, 0, 0, 0.02)',
                     borderRadius: 1,
                  }}
               >
                  <Checkbox
                     checked={selectedParticipants.length === members.length && members.length > 0}
                     indeterminate={
                        selectedParticipants.length > 0 && selectedParticipants.length < members.length
                     }
                     onChange={(e) => {
                        if (e.target.checked) {
                           setSelectedParticipants(members);
                        } else {
                           setSelectedParticipants([]);
                        }
                     }}
                     disabled={sending || members.length === 0}
                  />
                  <Typography variant='body2' sx={{ fontWeight: 500 }}>
                     Select All Participants ({members.length})
                  </Typography>
               </Box>

               <Autocomplete
                  multiple
                  options={members}
                  value={selectedParticipants}
                  onChange={(event, newValue) => {
                     setSelectedParticipants(newValue);
                  }}
                  getOptionLabel={(option) =>
                     option.profileData?.displayName ||
                     option.profileData?.fullName ||
                     option.profileData?.name ||
                     option.displayName ||
                     option.name ||
                     option.email ||
                     'Unknown User'
                  }
                  renderInput={(params) => (
                     <TextField
                        {...params}
                        label='Select Participants'
                        placeholder='Choose participants to send reminder'
                        helperText='Select one or more participants to receive the reminder'
                     />
                  )}
                  renderTags={(value, getTagProps) =>
                     value.map((option, index) => (
                        <Chip
                           label={
                              option.profileData?.displayName ||
                              option.profileData?.fullName ||
                              option.displayName ||
                              option.name ||
                              option.email
                           }
                           {...getTagProps({ index })}
                           key={option.id}
                        />
                     ))
                  }
                  disabled={sending}
                  sx={{ mb: 1 }}
               />
               <Typography variant='caption' color='text.secondary'>
                  {selectedParticipants.length} participant{selectedParticipants.length !== 1 ? 's' : ''} selected
               </Typography>
            </Box>

            {/* Message Input */}
            <Box sx={{ mb: 3 }}>
               <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label='Message (Optional)'
                  placeholder='Type your message here...'
                  value={message}
                  onChange={(e) => onMessageChange(e.target.value)}
                  disabled={sending}
                  sx={{ mb: 1 }}
               />
               <Typography variant='caption' color='text.secondary'>
                  Add a personal message to your reminder email (optional)
               </Typography>
            </Box>

            {/* Photo Selection */}
            <Box>
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'space-between',
                     alignItems: 'center',
                     mb: 2,
                  }}
               >
                  <Typography variant='subtitle2'>Select Photos (Optional - Max 5)</Typography>
                  <Typography
                     variant='caption'
                     color={selectedPhotos.size >= MAX_PHOTO_SELECTION ? 'error' : 'primary'}
                     sx={{ fontWeight: 600 }}
                  >
                     {selectedPhotos.size}/{MAX_PHOTO_SELECTION} selected
                  </Typography>
               </Box>

               {selectedPhotos.size >= MAX_PHOTO_SELECTION && (
                  <Alert severity='info' sx={{ mb: 2 }}>
                     Maximum of {MAX_PHOTO_SELECTION} photos reached. Deselect a photo to choose
                     another.
                  </Alert>
               )}

               {photos.length === 0 && !loadingPhotos ? (
                  <Box
                     sx={{
                        py: 4,
                        textAlign: 'center',
                        color: 'text.secondary',
                     }}
                  >
                     <Typography variant='body2'>No photos available in this album</Typography>
                  </Box>
               ) : (
                  <Box
                     ref={photoGridRef}
                     sx={{
                        maxHeight: '400px',
                        overflowY: 'auto',
                        pr: 1,
                        '&::-webkit-scrollbar': {
                           width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                           backgroundColor: '#f1f1f1',
                           borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                           backgroundColor: '#888',
                           borderRadius: '4px',
                           '&:hover': {
                              backgroundColor: '#555',
                           },
                        },
                     }}
                  >
                     <Grid container spacing={2}>
                        {photos.map((photo) => {
                           const isSelected = selectedPhotos.has(photo.id);
                           const isDisabled =
                              !isSelected && selectedPhotos.size >= MAX_PHOTO_SELECTION;

                           return (
                              <Grid item xs={6} sm={4} md={3} key={photo.id}>
                                 <Card
                                    sx={{
                                       position: 'relative',
                                       cursor: isDisabled ? 'not-allowed' : 'pointer',
                                       borderRadius: 2,
                                       overflow: 'hidden',
                                       border: isSelected
                                          ? `3px solid ${theme.palette.primary.main}`
                                          : '3px solid transparent',
                                       opacity: isDisabled ? 0.5 : 1,
                                       transition: 'all 0.2s',
                                       '&:hover': {
                                          transform: isDisabled ? 'none' : 'scale(1.02)',
                                       },
                                    }}
                                    onClick={() => handlePhotoToggle(photo.id)}
                                 >
                                    <Checkbox
                                       checked={isSelected}
                                       disabled={isDisabled}
                                       sx={{
                                          position: 'absolute',
                                          top: 4,
                                          right: 4,
                                          zIndex: 1,
                                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                                          '&:hover': {
                                             bgcolor: 'rgba(255, 255, 255, 1)',
                                          },
                                       }}
                                       onClick={(e) => e.stopPropagation()}
                                       onChange={() => handlePhotoToggle(photo.id)}
                                    />
                                    <CardMedia
                                       component='img'
                                       height='140'
                                       image={photo.url}
                                       alt={photo.title || 'Photo'}
                                       sx={{
                                          objectFit: 'cover',
                                       }}
                                    />
                                 </Card>
                                 {photo.title && (
                                    <Typography
                                       variant='caption'
                                       sx={{
                                          display: 'block',
                                          mt: 0.5,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                       }}
                                    >
                                       {photo.title}
                                    </Typography>
                                 )}
                              </Grid>
                           );
                        })}
                     </Grid>

                     {/* Loading more photos indicator */}
                     {loadingPhotos && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                           <CircularProgress size={32} />
                           <Typography variant='caption' sx={{ ml: 2, mt: 1 }}>
                              Loading more photos...
                           </Typography>
                        </Box>
                     )}

                     {/* Load more button - shown when more photos available */}
                     {!loadingPhotos && hasMorePhotos && photos.length > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                           <Button
                              variant='outlined'
                              size='small'
                              onClick={() => {
                                 console.log('🖱️ Manual load more clicked');
                                 onLoadMorePhotos?.();
                              }}
                           >
                              Load More Photos
                           </Button>
                        </Box>
                     )}

                     {/* No more photos message */}
                     {!hasMorePhotos && photos.length > 0 && !loadingPhotos && (
                        <Typography
                           variant='caption'
                           color='text.secondary'
                           sx={{ display: 'block', textAlign: 'center', py: 2 }}
                        >
                           All photos loaded ({photos.length} total)
                        </Typography>
                     )}
                  </Box>
               )}
            </Box>
         </DialogContent>

         <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={onClose} disabled={sending} color='inherit'>
               Cancel
            </Button>
            <Button
               variant='contained'
               onClick={() => onSend(selectedParticipants.map((p) => p.id))}
               disabled={sending || selectedParticipants.length === 0}
               startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
            >
               {sending ? 'Sending...' : 'Send Reminder'}
            </Button>
         </DialogActions>
      </Dialog>
   );
};

export default ParticipantReminderModal;

